import { Capacitor } from '@capacitor/core'
import { faClose } from '@fortawesome/pro-regular-svg-icons/faClose'
import { t } from '@lingui/macro'
import { motion } from 'framer-motion'
import { useCallback, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'

import { Card } from '@/@bothrs-ui/web/components/card/Card'
import { FlexColumn, FlexRow, Margin } from '@/@bothrs-ui/web/components/layout/index'
import { Button } from '@/@bothrs-ui/web/components/primitives/button/Button'
import { IconButton } from '@/@bothrs-ui/web/components/primitives/icon-button/IconButton'
import { Span } from '@/@bothrs-ui/web/components/typography/Body'
import { H4 } from '@/@bothrs-ui/web/components/typography/Heading'
import { usePushNotifications } from '@/capacitor/hooks/usePushNotifications'
import { mixpanel } from '@/mixpanel'
import { NotificationPreModal } from '@/modules/notifications/components/NotificationPreModal'
import { NotificationSettingsModal } from '@/modules/notifications/components/NotificationSettingsModal'
import { useNotificationsEnroll } from '@/modules/notifications/state/useNotificationsEnroll'
import { getDropInProps } from '@/modules/shared/animations/dropIn.animation'

import type { MouseEventHandler } from 'react'

const NotificationsCard = () => {
  // State
  // ------------------------------------------------------------------------- /
  const [showModal, setShowModal] = useState<boolean>(false)
  const [showFollowUpModal, setShowFollowUpModal] = useState<boolean>(false)

  // Hooks
  // ------------------------------------------------------------------------- /
  const theme = useTheme()
  const { notificationEnrollState, setNotificationEnrollState } = useNotificationsEnroll()

  const navigate = useNavigate()
  const { requestPermissions, permissions } = usePushNotifications()

  // Interaction Methods
  // ------------------------------------------------------------------------- /
  const handleOpenNotification = useCallback(() => {
    setShowModal(true)
    mixpanel.track('push_notification_enable_start', { origin: 'home_feed' })
  }, [])

  const handleModalAction = useCallback(async () => {
    await requestPermissions()
    setShowModal(false)
    setNotificationEnrollState({ interacted: true, dismissed: false })
  }, [setNotificationEnrollState, requestPermissions])

  const handleFollowUpModalAction = useCallback(async () => {
    mixpanel.track('push_notification_enable_submit')
    setShowFollowUpModal(false)
    navigate('/profile/privacy')
  }, [navigate])

  const handleModalClose = useCallback(() => {
    mixpanel.track('push_notification_enable_cancel')
    setShowModal(false)
    setShowFollowUpModal(true)
  }, [])

  const handleFollowUpModalClose = useCallback(() => {
    setShowFollowUpModal(false)
    setNotificationEnrollState({ interacted: true, dismissed: true })
  }, [setNotificationEnrollState])

  const handleCardDismiss: MouseEventHandler<HTMLButtonElement> = useCallback(
    (event) => {
      event.stopPropagation()
      setNotificationEnrollState({
        dismissed: true,
        interacted: true,
      })
    },
    [setNotificationEnrollState],
  )

  // Render
  // ------------------------------------------------------------------------- /
  if (
    !Capacitor.isNativePlatform() ||
    notificationEnrollState.interacted ||
    permissions?.receive === 'granted' ||
    permissions?.receive === 'denied'
  ) {
    return null
  }

  return (
    <motion.div {...getDropInProps()}>
      <StCardWrapper>
        <StCloseContainer>
          <IconButton variant='Tertiary' icon={faClose} size='large' onClick={handleCardDismiss} />
        </StCloseContainer>

        <StContentWrapper>
          <Button
            variant='Tertiary'
            onClick={() => {
              handleOpenNotification()
            }}
          >
            <FlexRow alignItems={'flex-start'}>
              <Margin mr={theme.UI.Spacing.Space4}>
                <img src='/img/svg/notification-bell.svg' alt='' />
              </Margin>
              <FlexColumn>
                <H4>
                  {t({
                    id: 'notifications.enrollment-card.title',
                    comment: 'title for the notifications card',
                    message: 'Activeer je meldingen en blijf op de hoogte?',
                  })}
                </H4>
                <Span type='BodyMediumRegular'>
                  {t({
                    id: 'notifications.enrollment-card.body',
                    comment: 'body for the notifications card',
                    message: 'Activeer meldingen om altijd te weten wat je vandaag moet doen.',
                  })}
                </Span>
              </FlexColumn>
            </FlexRow>
          </Button>
        </StContentWrapper>
      </StCardWrapper>

      <NotificationPreModal isVisible={showModal} onClose={handleModalClose} onAction={handleModalAction} />
      <NotificationSettingsModal
        isVisible={showFollowUpModal}
        onClose={handleFollowUpModalClose}
        onAction={handleFollowUpModalAction}
      />
    </motion.div>
  )
}

export { NotificationsCard }

const StCardWrapper = styled(Card)`
  position: relative;
`

const StCloseContainer = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.UI.SpacingPx.Space2};
  right: ${({ theme }) => theme.UI.SpacingPx.Space2};
  z-index: 1;
`

const StContentWrapper = styled.div`
  padding-right: ${({ theme }) => theme.UI.SpacingPx.Space12};
`
